/**
 * The pain editor (`PUT /me/injuries`, the whole list) and the PAR-Q
 * (`PUT /me/parq`).
 */
import { injuriesOptions, parqOptions } from "@repo/client/data/profile";
import {
  type InjuryInput,
  type InjuryListInput,
  injuryListInput,
  type ParqAnswers,
  type ParqInput,
  parqInput,
} from "@repo/contract/training";
import {
  "profile-editors.error_field" as errorField,
  "profile-editors.eyebrow_health" as eyebrowHealth,
  "profile-editors.eyebrow_pain_edit" as eyebrowPainEdit,
  "profile-editors.eyebrow_pain_new" as eyebrowPainNew,
  "profile-editors.pain_remove" as painRemove,
  "profile-editors.pain_removed" as painRemoved,
  "profile-editors.pain_saved_edit" as painSavedEdit,
  "profile-editors.pain_saved_new" as painSavedNew,
  "profile-editors.pain_title" as painTitle,
  "profile-editors.parq_save" as parqSave,
  "profile-editors.parq_saved" as parqSaved,
  "profile-editors.parq_title" as parqTitle,
  "profile-editors.register" as registerText,
  "profile-editors.save" as saveText,
} from "@repo/i18n";
import { useCallback } from "react";
import { useApi } from "../../../data/api";
import { Button } from "../../../ui/controls";
import {
  type EditorHost,
  EditorShell,
  useSaveFlow,
  useSectionRead,
} from "./editor-shell";
import { PainForm } from "./pain-form";
import {
  applyPainEdit,
  emptyPainForm,
  type PainForm as PainDraft,
  painFormErrors,
  painFormFrom,
  removePain,
} from "./pain-model";
import { NO_PARQ, ParqForm } from "./parq-form";
import { useEditorForm } from "./use-editor-form";
import { useInjuriesMutation, useParqMutation } from "./use-editor-mutation";
import { hasErrorAt } from "./validate";

const NO_INJURIES: readonly InjuryInput[] = [];

/**
 * `pain-new` (index `null`) or `pain:<index>`. The PUT carries every other
 * stored report unchanged (`applyPainEdit`); a missing answer is a field
 * error under its question (`painFormErrors`), never a thrown exception.
 */
export function PainEditor({
  host,
  index,
}: {
  readonly host: EditorHost;
  readonly index: number | null;
}) {
  const api = useApi();
  const read = useSectionRead(injuriesOptions(api));
  const mutation = useInjuriesMutation();
  const flow = useSaveFlow(mutation, host);
  const stored = read.data?.injuries ?? NO_INJURIES;
  const editing =
    index !== null && index >= 0 && index < stored.length ? index : null;
  const current = editing === null ? null : (stored[editing] ?? null);

  const form = useEditorForm({
    onSubmit: (payload: InjuryListInput) =>
      flow.save(payload, () => ({
        message: editing === null ? painSavedNew() : painSavedEdit(),
      })),
    precheck: painFormErrors,
    schema: injuryListInput,
    server: current,
    toDraft: (injury: InjuryInput | null) =>
      injury === null ? emptyPainForm() : painFormFrom(injury),
    toPayload: (draft: PainDraft) => applyPainEdit(stored, editing, draft),
  });

  const { save } = flow;
  const remove = useCallback(() => {
    if (editing !== null) {
      save(removePain(stored, editing), () => ({ message: painRemoved() }));
    }
  }, [editing, save, stored]);

  const contractError = hasErrorAt(form.errors, "injuries");
  return (
    <EditorShell
      eyebrow={editing === null ? eyebrowPainNew() : eyebrowPainEdit()}
      failure={flow.failure ?? (contractError ? errorField() : null)}
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      saveLabel={editing === null ? registerText() : saveText()}
      title={painTitle()}
    >
      <PainForm
        errors={form.errors}
        onChange={form.setDraft}
        value={form.draft}
      />
      {editing === null ? null : (
        <Button
          className="mt-4 w-full text-danger hover:bg-danger-soft"
          disabled={flow.pending}
          onClick={remove}
          variant="ghost"
        >
          {painRemove()}
        </Button>
      )}
    </EditorShell>
  );
}

export function ParqEditor({ host }: { readonly host: EditorHost }) {
  const api = useApi();
  const read = useSectionRead(parqOptions(api));
  const flow = useSaveFlow(useParqMutation(), host);
  const form = useEditorForm({
    onSubmit: (payload: ParqInput) =>
      flow.save(payload, () => ({ message: parqSaved() })),
    schema: parqInput,
    server: read.data?.answers ?? null,
    toDraft: (answers: ParqAnswers | null) => answers ?? NO_PARQ,
    toPayload: (answers: ParqAnswers) => ({ answers }),
  });
  return (
    <EditorShell
      eyebrow={eyebrowHealth()}
      failure={
        flow.failure ??
        (Object.keys(form.errors).length > 0 ? errorField() : null)
      }
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      saveLabel={parqSave()}
      title={parqTitle()}
    >
      <ParqForm onChange={form.setDraft} value={form.draft} />
    </EditorShell>
  );
}
